import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTodayFlowState, saveTodayFlowState, subscribeToFlowUpdates } from '../utils/unityFlowStorage';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const STORAGE_KEY = 'unitylove_today_flow_v1';
const STREAK_STORAGE_KEY = 'unitylove_today_streak_v1';

// Helper: get YYYY-MM-DD string
function getTodayDateString() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper: get localStorage key for discover attempt completion
function getDiscoverAttemptKey() {
  return `unitylove:streak:${getTodayDateString()}:discover_attempt`;
}

// Check if discover attempt was made today
function hasDiscoverAttemptToday() {
  try {
    return localStorage.getItem(getDiscoverAttemptKey()) === "1";
  } catch {
    return false;
  }
}

// Mark discover attempt as complete for today
function markDiscoverAttempt() {
  try {
    localStorage.setItem(getDiscoverAttemptKey(), "1");
  } catch (e) {
    console.error('[TodayFlow] Failed to mark discover attempt', e);
  }
}

// Load streak state from localStorage
const loadStreakState = () => {
  try {
    const raw = localStorage.getItem(STREAK_STORAGE_KEY);
    if (!raw) {
      return {
        lastCompletedDate: null,
        streak: 0,
        hasUnclaimedReward: false,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      lastCompletedDate: parsed.lastCompletedDate || null,
      streak: typeof parsed.streak === 'number' ? parsed.streak : 0,
      hasUnclaimedReward: !!parsed.hasUnclaimedReward,
    };
  } catch (e) {
    console.error('[TodayFlow] Failed to parse streak state', e);
    return {
      lastCompletedDate: null,
      streak: 0,
      hasUnclaimedReward: false,
    };
  }
};

const saveStreakState = (state) => {
  try {
    localStorage.setItem(STREAK_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('[TodayFlow] Failed to save streak state', e);
  }
};

// Difference in days between two YYYY-MM-DD strings
const diffDays = (from, to) => {
  if (!from || !to) return Infinity;
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const d1 = new Date(fy, fm - 1, fd);
  const d2 = new Date(ty, tm - 1, td);
  const diffMs = d2.getTime() - d1.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

// 🎯 Report UX events to backend for AI Guardians monitoring
async function reportUxEvent(event) {
  try {
    const token = localStorage.getItem('token');
    await fetch(`${window.location.origin}/api/guardians/ux-event`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(event),
    });
  } catch {
    // swallow errors; monitoring should never break UX
  }
}

// 🎯 CANONICAL STEP KEYS - Single source of truth for step identification
const CANONICAL_STEPS = {
  checkLikes: 'check_likes',
  sendFlirt: 'send_flirt',
  visitLounge: 'visit_lounge',
};

// Normalize backend flow data to canonical format
// Now handles /api/unity-flow/status shape as canonical source
function normalizeBackendFlow(dailyFlow) {
  const completed = {};
  
  // 🎯 CANONICAL: Handle tasks_done from /api/unity-flow/status
  // This is the single source of truth
  if (dailyFlow?.tasks_done) {
    if (dailyFlow.tasks_done.LIKES) completed[CANONICAL_STEPS.checkLikes] = true;
    if (dailyFlow.tasks_done.FLIRT_SENT) completed[CANONICAL_STEPS.sendFlirt] = true;
    if (dailyFlow.tasks_done.LOUNGE_VISIT) completed[CANONICAL_STEPS.visitLounge] = true;
  }
  
  // Legacy fallback: Handle dailyFlow.send_flirt_or_like (from old /api/daily-flow/status)
  if (dailyFlow?.send_flirt_or_like?.completed) {
    completed[CANONICAL_STEPS.sendFlirt] = true;
  }
  
  // Legacy fallback: Handle tasks object (from old /api/unity-flow/streak)
  if (dailyFlow?.tasks) {
    if (dailyFlow.tasks.flirt?.completed) completed[CANONICAL_STEPS.sendFlirt] = true;
    if (dailyFlow.tasks.likes?.completed || dailyFlow.tasks.check_likes?.completed) completed[CANONICAL_STEPS.checkLikes] = true;
    if (dailyFlow.tasks.lounge?.completed || dailyFlow.tasks.visit_lounge?.completed) completed[CANONICAL_STEPS.visitLounge] = true;
  }
  
  return completed;
}

// Normalize localStorage state to canonical format
function normalizeLocalState(localState) {
  const completed = {};
  
  // Map localStorage keys to canonical keys
  if (localState?.checkedLikes) completed[CANONICAL_STEPS.checkLikes] = true;
  if (localState?.sentFlirt) completed[CANONICAL_STEPS.sendFlirt] = true;
  if (localState?.joinedLounge) completed[CANONICAL_STEPS.visitLounge] = true;
  
  return completed;
}

const TodayFlow = ({ dailyFlow }) => {
  const navigate = useNavigate();

  const [localState, setLocalState] = useState(() => {
    const stored = getTodayFlowState();
    return stored.state;
  });

  const [isComplete, setIsComplete] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  
  // 🎯 PATCH: Track discover attempt completion (attempt-based, not proof-based)
  const [discoverAttempted, setDiscoverAttempted] = useState(() => hasDiscoverAttemptToday());

  // Streak state
  const [streak, setStreak] = useState(0);
  const [showRewardModal, setShowRewardModal] = useState(false);
  
  // 🎯 Milestone-based rewards (5/10/20 days)
  const [nextClaimableMilestone, setNextClaimableMilestone] = useState(null);
  const [claimableBonusMinutes, setClaimableBonusMinutes] = useState(0);
  const [milestones, setMilestones] = useState({});
  const [isClaiming, setIsClaiming] = useState(false);
  
  // 🎯 PATCH: Re-check discover attempt on visibility change (tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setDiscoverAttempted(hasDiscoverAttemptToday());
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // 🎯 SINGLE SOURCE OF TRUTH: Merge backend + localStorage, backend wins on conflicts
  const backendCompleted = normalizeBackendFlow(dailyFlow);
  const localCompleted = normalizeLocalState(localState);
  
  // Merge: backend truth + localStorage for steps backend doesn't track
  // 🎯 PATCH: sendFlirt step also completes on discover attempt (CTA navigation)
  const completedSteps = {
    [CANONICAL_STEPS.checkLikes]: backendCompleted[CANONICAL_STEPS.checkLikes] || localCompleted[CANONICAL_STEPS.checkLikes] || false,
    [CANONICAL_STEPS.sendFlirt]: backendCompleted[CANONICAL_STEPS.sendFlirt] || localCompleted[CANONICAL_STEPS.sendFlirt] || discoverAttempted || false,
    [CANONICAL_STEPS.visitLounge]: backendCompleted[CANONICAL_STEPS.visitLounge] || localCompleted[CANONICAL_STEPS.visitLounge] || false,
  };
  
  // 🎯 UI MISMATCH DETECTOR - Report to AI Guardians when counts don't match
  const uiCompletedCount = Object.values(completedSteps).filter(Boolean).length;
  const serverCompletedCount = Object.values(backendCompleted).filter(Boolean).length;
  const localCompletedCount = Object.values(localCompleted).filter(Boolean).length;
  
  useEffect(() => {
    // Only report if there's a meaningful mismatch (both sources have data but disagree)
    if (serverCompletedCount > 0 && localCompletedCount > 0 && serverCompletedCount !== localCompletedCount) {
      const payload = {
        type: 'UNITY_FLOW_UI_MISMATCH',
        ts: Date.now(),
        serverCount: serverCompletedCount,
        uiCount: uiCompletedCount,
        localCount: localCompletedCount,
        serverCompleted: backendCompleted,
        localCompleted: localCompleted,
        mergedCompleted: completedSteps,
      };
      
      console.warn('[TodayFlow] UNITY_FLOW_UI_MISMATCH detected:', payload);
      reportUxEvent(payload);
    }
  }, [serverCompletedCount, localCompletedCount, uiCompletedCount, backendCompleted, localCompleted, completedSteps]);

  // 🎯 Event-driven updates (replaces 500ms polling)
  // Uses BroadcastChannel for cross-tab + CustomEvent for same-tab
  useEffect(() => {
    const unsubscribe = subscribeToFlowUpdates((data) => {
      console.log('[TodayFlow] Received flow update:', data);
      const stored = getTodayFlowState();
      setLocalState(stored.state);
    });
    return unsubscribe;
  }, []);

  // Load streak + milestone state from backend on mount
  // 🎯 CANONICAL: Use /api/unity-flow/status as single source of truth
  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // 🎯 Use unity-flow/status as canonical source
        const response = await fetch(
          `${window.location.origin}/api/unity-flow/status`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const streakDays = data.streak_current || 0;
          console.log('✅ Loaded streak from unity-flow/status:', streakDays, 'milestones:', data.milestones);
          
          setStreak(streakDays);
          setNextClaimableMilestone(data.next_claimable_milestone || null);
          setClaimableBonusMinutes(data.claimable_bonus_minutes || 0);
          setMilestones(data.milestones || {});
        } else {
          console.warn('⚠️ Failed to fetch streak:', response.status);
          // Fallback to localStorage for backward compatibility
          const streakState = loadStreakState();
          setStreak(streakState.streak || 0);
        }
      } catch (err) {
        console.error('❌ Error fetching streak:', err);
        const streakState = loadStreakState();
        setStreak(streakState.streak || 0);
      }
    };

    fetchStreak();
    
    // 🔥 Listen for streak updates from other components
    const handleStreakUpdate = (event) => {
      console.log('📡 Received streakUpdated event:', event.detail);
      if (typeof event.detail.streak === 'number') {
        setStreak(event.detail.streak);
      }
      // Also update milestone info if provided
      if (event.detail.next_claimable_milestone !== undefined) {
        setNextClaimableMilestone(event.detail.next_claimable_milestone);
        setClaimableBonusMinutes(event.detail.claimable_bonus_minutes || 0);
      }
    };
    
    window.addEventListener('streakUpdated', handleStreakUpdate);
    
    return () => {
      window.removeEventListener('streakUpdated', handleStreakUpdate);
    };
  }, []);

  // Handle claiming milestone bonus
  const handleClaimMilestone = async () => {
    if (isClaiming || !nextClaimableMilestone) return;
    
    setIsClaiming(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(
        `${window.location.origin}/api/lounge/redeem-streak-bonus`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok || data.duplicate) {
        console.error('[TodayFlow] Claim milestone failed:', data);
        alert(data.message || 'Couldn\'t claim your bonus. Try again later.');
      } else {
        alert(data.message || `🎉 Claimed +${data.awarded_minutes} minutes!`);
        // Update local state with new milestone info
        setNextClaimableMilestone(data.next_claimable_milestone || null);
        setClaimableBonusMinutes(data.claimable_bonus_minutes || 0);
        setMilestones(data.milestones || {});
        setShowRewardModal(false);
        // Note: Streak is NOT reset - it continues growing
        setStreak(data.streak_current || streak);
      }
    } catch (err) {
      console.error('[TodayFlow] Claim milestone error:', err);
      alert('Couldn\'t claim your bonus. Try again later.');
    } finally {
      setIsClaiming(false);
    }
  };

  // 🎯 Handle flow completion - fetch updated status from canonical source
  const handleFlowCompletedForToday = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch current status from canonical source
      const statusResponse = await fetch(
        `${window.location.origin}/api/unity-flow/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (statusResponse.ok) {
        const data = await statusResponse.json();
        const newStreak = data.streak_current || 0;
        console.log('✅ Unity Flow completed! Streak:', newStreak, 'Next milestone:', data.next_claimable_milestone);
        
        setStreak(newStreak);
        setNextClaimableMilestone(data.next_claimable_milestone || null);
        setClaimableBonusMinutes(data.claimable_bonus_minutes || 0);
        setMilestones(data.milestones || {});

        // Dispatch event to update other components
        window.dispatchEvent(new CustomEvent('streakUpdated', { 
          detail: { 
            streak: newStreak,
            next_claimable_milestone: data.next_claimable_milestone,
            claimable_bonus_minutes: data.claimable_bonus_minutes,
          } 
        }));

        // Show reward modal if there's a claimable milestone
        if (data.next_claimable_milestone) {
          setShowRewardModal(true);
        }
      } else {
        console.warn('⚠️ Failed to fetch Unity Flow status:', statusResponse.status);
      }
    } catch (err) {
      console.error('❌ Failed to fetch Unity Flow status:', err);
    }
  };

  // Persist state + handle completion transitions
  useEffect(() => {
    saveTodayFlowState(localState);

    // 🎯 Use SINGLE SOURCE OF TRUTH for completion check
    const allDone = 
      completedSteps[CANONICAL_STEPS.checkLikes] && 
      completedSteps[CANONICAL_STEPS.sendFlirt] && 
      completedSteps[CANONICAL_STEPS.visitLounge];

    // Transition from incomplete -> complete
    if (allDone && !isComplete) {
      setIsComplete(true);
      setJustCompleted(true);
      // Let the little celebration pill glow for a few seconds
      const t = setTimeout(() => setJustCompleted(false), 3000);
      
      // 🔥 Streak bump when flow completed for today
      handleFlowCompletedForToday();
      
      return () => clearTimeout(t);
    }

    // If we somehow go back to incomplete, reset flags
    if (!allDone && isComplete) {
      setIsComplete(false);
      setJustCompleted(false);
    }
  }, [localState, completedSteps, isComplete]);

  // Note: Auto-complete is now handled by each page using markFlowStep() from unityFlowStorage
  // WhoLikedMe.js marks 'checkedLikes', Discover.js marks 'sentFlirt', LifestyleLounge.js marks 'joinedLounge'

  // 🎯 PATCH: Removed toggleStep - circles are now display-only, no click-to-green
  // Completion is tracked via:
  // 1. Backend (tasks_done from /api/unity-flow/status)
  // 2. localStorage via markFlowStep() from individual pages
  // 3. Discover attempt flag (CTA click marks completion)

  // 🎯 PATCH: Handler for discover CTA - marks attempt before navigating
  const handleDiscoverCTA = () => {
    markDiscoverAttempt();
    setDiscoverAttempted(true);
    navigate('/discover');
  };

  // 🎯 Steps with canonical keys - single source of truth
  const steps = [
    {
      id: 'likes',
      canonicalKey: CANONICAL_STEPS.checkLikes,
      title: 'Check who liked you',
      description: "See who's already interested in you.",
      storageKey: 'checkedLikes',
      gradient: 'from-pink-500 to-purple-500',
      onGo: () => navigate('/who-liked-me'),
    },
    {
      id: 'flirt',
      canonicalKey: CANONICAL_STEPS.sendFlirt,
      title: 'Send a flirt or like',
      description: 'Reach out to someone that caught your eye.',
      storageKey: 'sentFlirt',
      gradient: 'from-purple-500 to-indigo-500',
      onGo: handleDiscoverCTA, // 🎯 PATCH: Uses attempt-based completion
    },
    {
      id: 'lounge',
      canonicalKey: CANONICAL_STEPS.visitLounge,
      title: 'Visit the lifestyle lounge',
      description: 'Spend a few minutes in the video lounge.',
      storageKey: 'joinedLounge',
      gradient: 'from-indigo-500 to-pink-500',
      onGo: () => navigate('/lifestyle-lounge'),
    },
  ];
  
  // 🎯 Helper to check if a step is done using single source of truth
  const isStepDone = (step) => !!completedSteps[step.canonicalKey];

  return (
    <div className="mb-6" data-testid="todayflow">
      <div className="rounded-2xl bg-slate-900 text-slate-100 border border-pink-500/30 shadow-md px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
              <span>Today&apos;s Unity Flow</span>
              {streak > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="flex items-center gap-1 text-sm text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/40 cursor-help"
                        tabIndex={0}
                        aria-label={`Streak: ${streak} days. Earned after consecutive days of activity.`}
                      >
                        <span className="text-lg leading-none">🔥</span>
                        <span>{streak}</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-slate-800 text-white border-slate-700 max-w-[200px]">
                      <p className="text-xs">Streak Bonus: earned after consecutive days of activity.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300">
              Follow these 3 steps each day to keep your streak alive.
            </p>
          </div>

          {/* Streak reward indicator - right side */}
          {nextClaimableMilestone && (
            <span className="text-[11px] text-emerald-300 flex-shrink-0">
              Streak reward ready
            </span>
          )}
        </div>
        
        {/* Flow Complete Badge - Centered */}
        {isComplete && (
          <div className="w-full flex justify-center -mt-2 mb-2" data-testid="flow-complete-badge">
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${
                justCompleted
                  ? 'border-emerald-400 bg-emerald-500/10 text-emerald-200 shadow-[0_0_15px_rgba(16,185,129,0.6)] animate-pulse'
                  : 'border-emerald-500/60 bg-emerald-500/5 text-emerald-100'
              }`}
            >
              Flow complete 🎉
            </span>
          </div>
        )}

        <div className="grid gap-3 sm:gap-4 sm:grid-cols-3">
          {steps.map((step) => {
            // 🎯 SINGLE SOURCE OF TRUTH: Use merged completedSteps
            const done = isStepDone(step);
            return (
              <div
                key={step.id}
                className="flex flex-col items-start rounded-xl bg-slate-800/90 hover:bg-slate-800 transition-colors border border-slate-700 px-4 py-3 text-left"
                data-testid={`flow-step-${step.id}`}
              >
                {/* Checkbox + title */}
                <div className="flex items-center gap-3 w-full mb-2">
                  {/* 🎯 PATCH: Circle is display-only, no click handler, no cursor-pointer */}
                  <div
                    className={
                      'flex h-5 w-5 items-center justify-center rounded-full border transition-colors cursor-default select-none ' +
                      (done
                        ? 'border-emerald-400 bg-emerald-500'
                        : 'border-slate-500 bg-slate-900')
                    }
                    aria-label={done ? 'Step complete' : 'Step incomplete'}
                    data-testid={`flow-circle-${step.id}`}
                  >
                    {done && (
                      <span className="text-[11px] text-white font-semibold">
                        ✓
                      </span>
                    )}
                  </div>

                  <div className="flex-1">
                    <p className="text-sm font-semibold">{step.title}</p>
                    <p className="text-xs text-slate-300 mt-0.5">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Footer row: Go button + done badge */}
                <div className="mt-1 flex items-center justify-between w-full">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      step.onGo();
                    }}
                    className={
                      'inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r ' +
                      step.gradient
                    }
                  >
                    Go
                  </button>

                  {done && (
                    <span className="text-[11px] text-emerald-300 font-medium">
                      Done
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {isComplete && (
          <div className="mt-3 sm:hidden">
            <div
              className={
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border border-emerald-400/60 text-emerald-200 bg-emerald-500/10 ' +
                (justCompleted ? 'animate-pulse shadow-[0_0_15px_rgba(16,185,129,0.6)]' : '')
              }
            >
              <span>Flow complete</span>
              <span role="img" aria-label="celebration">
                🎉
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Milestone Reward Modal */}
      {showRewardModal && nextClaimableMilestone && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-emerald-500/40 p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-emerald-200 mb-1 flex items-center gap-2">
              <span className="text-2xl">🔥</span>
              <span>{nextClaimableMilestone}-Day Streak Milestone!</span>
            </h3>
            <p className="text-sm text-slate-300 mb-3">
              Amazing! You&apos;ve reached a <span className="font-semibold">{streak}</span>-day streak!
              Claim your <span className="font-semibold text-emerald-300">+{claimableBonusMinutes} minute bonus</span> in the Lifestyle Lounge.
            </p>
            
            {/* Milestone progress */}
            <div className="flex items-center gap-2 mb-4">
              {[5, 10, 20].map((m) => {
                const mStatus = milestones[String(m)]?.status || 'locked';
                return (
                  <div
                    key={m}
                    className={`flex-1 text-center py-2 rounded-lg text-xs font-medium ${
                      mStatus === 'claimed'
                        ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/50'
                        : mStatus === 'claimable'
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50 animate-pulse'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    }`}
                  >
                    <div>Day {m}</div>
                    <div className="text-[10px] opacity-70">
                      +{STREAK_MILESTONES[m] || m}min
                    </div>
                    {mStatus === 'claimed' && <span className="text-emerald-400">✓</span>}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-slate-400 mb-4">
              Your streak continues after claiming! Keep going for bigger rewards at Day 10 and Day 20.
            </p>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowRewardModal(false)}
                className="px-3 py-1.5 text-xs rounded-full border border-slate-600 text-slate-200 hover:bg-slate-800 transition"
              >
                Maybe later
              </button>
              <button
                type="button"
                onClick={handleClaimMilestone}
                disabled={isClaiming}
                className="px-4 py-1.5 text-xs rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-md shadow-emerald-500/40 transition disabled:opacity-50"
              >
                {isClaiming ? 'Claiming...' : `Claim +${claimableBonusMinutes} min`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Milestone config for UI
const STREAK_MILESTONES = { 5: 5, 10: 10, 20: 20 };

export default TodayFlow;
